/**
 * Comprehensive validation utilities for all forms in the application
 * Provides HTML5 validation attributes, custom validation rules, and error handling
 */

// Validation patterns
export const PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^\+?[1-9]\d{0,15}$/,
  URL: /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/,
  SLUG: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
  NAME: /^[a-zA-Z\s'-]+$/,
  PRICE: /^\d+(\.\d{1,2})?$/,
  TIME: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  DATE: /^\d{4}-\d{2}-\d{2}$/
};

// Validation rules configuration
export const VALIDATION_RULES = {
  // User registration and profile
  name: {
    required: true,
    minLength: 2,
    maxLength: 100,
    pattern: PATTERNS.NAME,
    messages: {
      required: 'Name is required',
      minLength: 'Name must be at least 2 characters',
      maxLength: 'Name must be less than 100 characters',
      pattern: 'Name can only contain letters, spaces, hyphens, and apostrophes'
    }
  },
  email: {
    required: true,
    pattern: PATTERNS.EMAIL,
    messages: {
      required: 'Email is required',
      pattern: 'Please enter a valid email address'
    }
  },
  phone: {
    required: false,
    pattern: PATTERNS.PHONE,
    minLength: 10,
    messages: {
      pattern: 'Please enter a valid phone number',
      minLength: 'Phone number must have at least 10 digits'
    }
  },
  password: {
    required: true,
    minLength: 8,
    pattern: PATTERNS.PASSWORD,
    messages: {
      required: 'Password is required',
      minLength: 'Password must be at least 8 characters',
      pattern: 'Password must contain uppercase, lowercase, number, and special character'
    }
  },
  confirmPassword: {
    required: true,
    messages: {
      required: 'Please confirm your password',
      mismatch: 'Passwords do not match'
    }
  },

  // Blog posts
  title: {
    required: true,
    minLength: 3,
    maxLength: 200,
    messages: {
      required: 'Title is required',
      minLength: 'Title must be at least 3 characters',
      maxLength: 'Title must be less than 200 characters'
    }
  },
  content: {
    required: true,
    minLength: 10,
    maxLength: 10000,
    messages: {
      required: 'Content is required',
      minLength: 'Content must be at least 10 characters',
      maxLength: 'Content must be less than 10,000 characters'
    }
  },
  excerpt: {
    required: false,
    maxLength: 500,
    messages: {
      maxLength: 'Excerpt must be less than 500 characters'
    }
  },
  status: {
    required: true,
    messages: {
      required: 'Status is required'
    }
  },

  // Classes
  classTitle: {
    required: true,
    minLength: 3,
    maxLength: 100,
    messages: {
      required: 'Class title is required',
      minLength: 'Class title must be at least 3 characters',
      maxLength: 'Class title must be less than 100 characters'
    }
  },
  description: {
    required: true,
    minLength: 10,
    maxLength: 1000,
    messages: {
      required: 'Description is required',
      minLength: 'Description must be at least 10 characters',
      maxLength: 'Description must be less than 1,000 characters'
    }
  },
  level: {
    required: true,
    messages: {
      required: 'Level is required'
    }
  },
  price: {
    required: true,
    pattern: PATTERNS.PRICE,
    min: 0,
    messages: {
      required: 'Price is required',
      pattern: 'Please enter a valid price',
      min: 'Price must be 0 or greater'
    }
  },
  capacity: {
    required: true,
    type: 'number',
    min: 1,
    max: 100,
    messages: {
      required: 'Capacity is required',
      min: 'Capacity must be at least 1',
      max: 'Capacity must be 100 or less'
    }
  },

  // Events
  eventTitle: {
    required: true,
    minLength: 3,
    maxLength: 100,
    messages: {
      required: 'Event title is required',
      minLength: 'Event title must be at least 3 characters',
      maxLength: 'Event title must be less than 100 characters'
    }
  },
  eventDate: {
    required: true,
    type: 'date',
    messages: {
      required: 'Event date is required',
      past: 'Event date cannot be in the past'
    }
  },
  startTime: {
    required: true,
    type: 'time',
    messages: {
      required: 'Start time is required'
    }
  },
  endTime: {
    required: true,
    type: 'time',
    messages: {
      required: 'End time is required',
      afterStart: 'End time must be after start time'
    }
  },

  // Store items
  productName: {
    required: true,
    minLength: 3,
    maxLength: 100,
    messages: {
      required: 'Product name is required',
      minLength: 'Product name must be at least 3 characters',
      maxLength: 'Product name must be less than 100 characters'
    }
  },
  productPrice: {
    required: true,
    pattern: PATTERNS.PRICE,
    min: 0.01,
    messages: {
      required: 'Price is required',
      pattern: 'Please enter a valid price',
      min: 'Price must be greater than 0'
    }
  },
  stock: {
    required: true,
    type: 'number',
    min: 0,
    messages: {
      required: 'Stock quantity is required',
      min: 'Stock cannot be negative'
    }
  },

  // Contact form
  subject: {
    required: false,
    maxLength: 200,
    messages: {
      maxLength: 'Subject must be less than 200 characters'
    }
  },
  message: {
    required: true,
    minLength: 10,
    maxLength: 2000,
    messages: {
      required: 'Message is required',
      minLength: 'Message must be at least 10 characters',
      maxLength: 'Message must be less than 2,000 characters'
    }
  }
};

/**
 * Validate a single field based on its rules
 * @param {string} value - Field value
 * @param {object} rules - Validation rules
 * @param {object} formData - Complete form data (for cross-field validation)
 * @returns {string|null} - Error message or null if valid
 */
export const validateField = (value, rules, formData = {}) => {
  if (!rules) return null;

  // Handle required validation
  if (rules.required && (!value || value.toString().trim() === '')) {
    return rules.messages.required;
  }

  // Skip other validations if field is empty and not required
  if (!value || value.toString().trim() === '') {
    return null;
  }

  const stringValue = value.toString().trim();

  // Length validations
  if (rules.minLength && stringValue.length < rules.minLength) {
    return rules.messages.minLength;
  }

  if (rules.maxLength && stringValue.length > rules.maxLength) {
    return rules.messages.maxLength;
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    return rules.messages.pattern;
  }

  // Number validations
  if (rules.type === 'number') {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return rules.messages.pattern || 'Please enter a valid number';
    }
    if (rules.min !== undefined && numValue < rules.min) {
      return rules.messages.min;
    }
    if (rules.max !== undefined && numValue > rules.max) {
      return rules.messages.max;
    }
  }

  // Date validations
  if (rules.type === 'date') {
    const dateValue = new Date(value);
    if (isNaN(dateValue.getTime())) {
      return 'Please enter a valid date';
    }
    if (rules.past === false && dateValue < new Date()) {
      return rules.messages.past;
    }
  }

  // Time validations
  if (rules.type === 'time') {
    if (!PATTERNS.TIME.test(value)) {
      return 'Please enter a valid time';
    }
  }

  // Cross-field validations
  if (rules.dependsOn) {
    const dependentValue = formData[rules.dependsOn];
    if (rules.dependsOn === 'startTime' && rules.afterStart) {
      if (value <= dependentValue) {
        return rules.messages.afterStart;
      }
    }
  }

  return null;
};

/**
 * Validate an entire form
 * @param {object} formData - Form data object
 * @param {object} validationSchema - Validation rules for each field
 * @returns {object} - Object with errors for each field
 */
export const validateForm = (formData, validationSchema) => {
  const errors = {};

  Object.keys(validationSchema).forEach(fieldName => {
    const fieldValue = formData[fieldName];
    const fieldRules = validationSchema[fieldName];
    
    const error = validateField(fieldValue, fieldRules, formData);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};

/**
 * Get HTML5 validation attributes for a field
 * @param {object} rules - Validation rules
 * @returns {object} - HTML5 attributes object
 */
export const getValidationAttributes = (rules) => {
  const attributes = {};

  if (rules.required) {
    attributes.required = true;
  }

  if (rules.minLength) {
    attributes.minLength = rules.minLength;
  }

  if (rules.maxLength) {
    attributes.maxLength = rules.maxLength;
  }

  if (rules.pattern) {
    attributes.pattern = rules.pattern.source;
  }

  if (rules.type === 'number') {
    attributes.type = 'number';
    if (rules.min !== undefined) {
      attributes.min = rules.min;
    }
    if (rules.max !== undefined) {
      attributes.max = rules.max;
    }
  }

  if (rules.type === 'email') {
    attributes.type = 'email';
  }

  if (rules.type === 'url') {
    attributes.type = 'url';
  }

  return attributes;
};

/**
 * Validate password confirmation
 * @param {string} password - Password value
 * @param {string} confirmPassword - Confirm password value
 * @returns {string|null} - Error message or null if valid
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  if (!confirmPassword) {
    return 'Please confirm your password';
  }
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }
  return null;
};

/**
 * Validate file upload
 * @param {File} file - File object
 * @param {object} options - Validation options
 * @returns {string|null} - Error message or null if valid
 */
export const validateFile = (file, options = {}) => {
  if (!file) {
    return options.required ? 'File is required' : null;
  }

  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    required = false
  } = options;

  if (required && !file) {
    return 'File is required';
  }

  if (file.size > maxSize) {
    return `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return `File type must be one of: ${allowedTypes.join(', ')}`;
  }

  return null;
};

/**
 * Sanitize input value
 * @param {string} value - Input value
 * @param {object} options - Sanitization options
 * @returns {string} - Sanitized value
 */
export const sanitizeInput = (value, options = {}) => {
  if (!value) return '';

  let sanitized = value.toString();

  const {
    removeHtml = true,
    maxLength = null,
    allowTags = [],
    trim = false
  } = options;

  // Only trim if explicitly requested (usually during submission, not during typing)
  if (trim) {
    sanitized = sanitized.trim();
  }

  if (removeHtml) {
    // Remove HTML tags except allowed ones
    const allowedTagsRegex = allowTags.length > 0 
      ? new RegExp(`<(?!/(?:${allowTags.join('|')})\\b)[^>]+>`, 'gi')
      : /<[^>]*>/g;
    sanitized = sanitized.replace(allowedTagsRegex, '');
  }

  if (maxLength && sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
};

/**
 * Format validation error for accessibility
 * @param {string} fieldName - Field name
 * @param {string} errorMessage - Error message
 * @returns {object} - Accessibility attributes
 */
export const getErrorAttributes = (fieldName, errorMessage) => {
  if (!errorMessage) return {};

  return {
    'aria-invalid': 'true',
    'aria-describedby': `${fieldName}-error`,
    'aria-errormessage': `${fieldName}-error`
  };
};

/**
 * Get success attributes for accessibility
 * @param {string} fieldName - Field name
 * @returns {object} - Accessibility attributes
 */
export const getSuccessAttributes = (fieldName) => {
  return {
    'aria-invalid': 'false',
    'aria-describedby': `${fieldName}-success`
  };
};

/**
 * Get validation hint for a field based on its type and rules
 * @param {string} fieldName - Field name
 * @param {object} rules - Validation rules
 * @param {string} fieldType - Field type (text, textarea, file, etc.)
 * @returns {string} - Validation hint message
 */
export const getValidationHint = (fieldName, rules, fieldType = 'text') => {
  const hints = {
    // User registration and profile
    name: 'Enter your full name (2-100 characters, letters only)',
    email: 'Enter a valid email address (e.g., user@example.com)',
    phone: 'Enter phone number (10+ digits, international format supported)',
    password: 'Password must be 8+ characters with uppercase, lowercase, number, and special character',
    confirmPassword: 'Re-enter your password to confirm',
    
    // Blog posts
    title: 'Enter a descriptive title (3-200 characters)',
    content: 'Write your post content (minimum 10 characters, maximum 10,000 characters)',
    excerpt: 'Brief summary of your post (optional, max 500 characters)',
    status: 'Select the publication status for your post',
    
    // Classes
    classTitle: 'Enter class title (3-100 characters)',
    description: 'Describe the class in detail (minimum 10 characters, maximum 1,000 characters)',
    level: 'Select the appropriate skill level for this class',
    price: 'Enter price in USD (e.g., 25.00)',
    capacity: 'Maximum number of students (1-100)',
    
    // Events
    eventTitle: 'Enter event title (3-100 characters)',
    eventDate: 'Select a future date for the event',
    startTime: 'Select the event start time',
    endTime: 'Select the event end time (must be after start time)',
    
    // Store items
    productName: 'Enter product name (3-100 characters)',
    productPrice: 'Enter price in USD (minimum $0.01)',
    stock: 'Available quantity (0 or more)',
    
    // Contact form
    subject: 'Brief subject line (optional, max 200 characters)',
    message: 'Your message (minimum 10 characters, maximum 2,000 characters)',
    
    // File uploads
    image_file: 'Upload image (JPEG, PNG, WebP, max 5MB)',
    profile_image: 'Upload profile photo (JPEG, PNG, WebP, max 5MB)',
    event_image: 'Upload event image (JPEG, PNG, WebP, max 5MB)',
    // class_image: 'Upload class image (JPEG, PNG, WebP, max 5MB)',
    
    // Generic hints based on field type
    text: rules?.minLength && rules?.maxLength 
      ? `Enter ${rules.minLength}-${rules.maxLength} characters`
      : rules?.minLength 
        ? `Minimum ${rules.minLength} characters`
        : rules?.maxLength 
          ? `Maximum ${rules.maxLength} characters`
          : 'Enter text',
    
    textarea: rules?.minLength && rules?.maxLength 
      ? `Write ${rules.minLength}-${rules.maxLength} characters`
      : rules?.minLength 
        ? `Minimum ${rules.minLength} characters`
        : rules?.maxLength 
          ? `Maximum ${rules.maxLength} characters`
          : 'Write your content',
    
    file: 'Select a file to upload',
    
    number: rules?.min !== undefined && rules?.max !== undefined
      ? `Enter a number between ${rules.min} and ${rules.max}`
      : rules?.min !== undefined
        ? `Enter a number ${rules.min} or greater`
        : rules?.max !== undefined
          ? `Enter a number ${rules.max} or less`
          : 'Enter a number',
    
    date: 'Select a date',
    
    time: 'Select a time'
  };

  // Return specific hint for the field name, or generic hint based on type
  return hints[fieldName] || hints[fieldType] || 'Enter appropriate value';
}; 